import React, { useMemo, useState, useRef, useEffect, createContext, useContext } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { ListChecks, BookOpen, Clock, Users, TrendingUp, TrendingDown, ArrowRight, RefreshCw, CheckCircle2, DollarSign, Target, Zap, Plus, FileText, Sparkles, Activity, Award, EyeOff, Eye, XCircle, RotateCcw, Bell, ListTodo, UserX, Check, CalendarX, Trophy, MessageSquare, Receipt, Sunrise, Sunset, ChevronDown } from "lucide-react";
import InfoTip from "../../components/InfoTip.jsx";
import SnoozeButton from "../../components/SnoozeButton.jsx";
import TrenerAbsenceCard from "../../components/TrenerAbsenceCard.jsx";
import KartaPoZkusebnim from "../../components/deal/DealDialogy.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { toVocativ } from "../../lib/czech.js";
import { toast } from "../../components/Toast.jsx";

// === TRENÉR DASHBOARD — Fáze 1 ===
// Akční nahoru, statistické dolů. Co dnes řeším → Co dál → Jak na tom jsem.
// Datové zdroje:
//   /api/trener-ukoly?pilot=X        — úkoly (deadline dnes/overdue)
//   /api/dokumenty?email=X           — nepřečtené/aktualizované dokumenty
//   /api/trainers?year=2026          — měsíční skore + klienti končící
// Fáze 2 (později): týdenní rytmus graf
// Fáze 3 (později): dnešní rozvrh tréninků z Reservio

function todayIso() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDaysIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function daysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const ms = new Date(toIso + "T00:00:00").getTime() - new Date(fromIso + "T00:00:00").getTime();
  return Math.round(ms / 86400000);
}
function fmtKc(v) {
  return new Intl.NumberFormat("cs-CZ").format(Math.round(v || 0));
}

// Pozdrav podle hodiny — bez "Dobrou noc", v noci ponecháme "Dobrý večer"
const HOUR_GREETING = (h) => {
  if (h >= 4 && h < 10) return "Dobré ráno";
  if (h >= 10 && h < 18) return "Dobrý den";
  return "Dobrý večer"; // večer + noc (18 → 24 → 4)
};

// Vokativ pro křestní jména trenérů. Pokrývá nejčastější české tvary + override mapou.
// toVocativ je teď v src/lib/czech.js — viz import nahoře.
const DAY_NAMES = ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"];
const MONTH_NAMES = ["ledna", "února", "března", "dubna", "května", "června", "července", "srpna", "září", "října", "listopadu", "prosince"];

export default function TrenerDashboard() {
  const { activeTrener, isOwner } = useOutletContext() || {};
  const user = getCurrentUser();
  const email = user?.email || "";
  const year = new Date().getFullYear();

  const { data: trainersData, refetch: refetchTrainers, refreshing: refreshingTrainers } = useApiData(`/api/trainers?year=${year}`);
  // Bez status filtru — chceme vsechny otevrene ukoly (naplanovane + v_procesu),
  // ne jen naplanovane. Hotovo/archiv se odfiltruje nize.
  const { data: ukolyData, refetch: refetchUkoly, refreshing: refreshingUkoly } = useApiData(`/api/trener-ukoly?pilot=${encodeURIComponent(activeTrener || "")}`);
  // POZOR: posíláme i ?pilot=activeTrener — při owner impersonaci se stav podpisu/přečtení počítá
  // za hraného trenéra (stejně jako stránka dokumentů), ne za přihlášeného ownera. Bez toho karta
  // ukazovala "K podpisu" i u dokumentů, které má hraný trenér už podepsané.
  const { data: docsData, refetch: refetchDocs, refreshing: refreshingDocs } = useApiData(
    (email || activeTrener) ? `/api/dokumenty?email=${encodeURIComponent(email || "")}&pilot=${encodeURIComponent(activeTrener || "")}` : null
  );
  // Klienti per pilot — pro neaktivní klienty (posledniTrenink > 14 dní)
  const { data: clientsData, refetch: refetchClients, refreshing: refreshingClients } = useApiData(activeTrener ? "/api/clients" : null);
  // Today definováno PŘED rozvrhDate aby nedošlo k ReferenceError (temporal dead zone)
  // Počet položek v kartě Po zkušebním (Deal u trenéra), ať nesvítí „Vše je v klidu“, když klientka čeká.
  const [poZkusebnimPocet, setPoZkusebnimPocet] = useState(0);
  const kartaPoZkusebnim = isOwner || activeTrener === (user?.linkedTrener || user?.name || "");
  useEffect(() => { setPoZkusebnimPocet(0); }, [activeTrener, kartaPoZkusebnim]);
  const today = todayIso();
  const in30Days = addDaysIso(today, 30);
  const ago14days = addDaysIso(today, -14);
  // Rozvrh — týdenní view (Po-Pá), navigace ±5 týdnů
  const [weekOffset, setWeekOffset] = useState(0);
  // Lokální skrytí čísel v "Tvoje skóre" (trenér si může schovat částky před někým).
  const [scoreHidden, setScoreHidden] = useState(false);
  const currentWeekMonday = useMemo(() => {
    const d = new Date(today + "T00:00:00");
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // posun na pondělí tohoto týdne
    d.setDate(d.getDate() + diff + weekOffset * 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [today, weekOffset]);
  // days=7 → backend vrátí Po-Ne. Frontend zobrazí Po-Pá vždy, So+Ne JEN pokud mají tréninky.
  const { data: rozvrhData, refetch: refetchRozvrh, refreshing: refreshingRozvrh } = useApiData(
    activeTrener ? `/api/trener-rozvrh?pilot=${encodeURIComponent(activeTrener)}&weekStart=${currentWeekMonday}&days=7` : null
  );

  // === CACHE pro instant přepínání týdnů a trenérů ===
  // Klíč: `${activeTrener}|${weekStart}` — per-trener + per-week. Cache zůstává napříč
  // přepínáními trenéra (přepínání tam-zpět = instantní). Žádné clear() při změně trenéra,
  // protože per-trener klíč zaručuje že nikdy nevracíme cizí data.
  const weekCacheRef = useRef(new Map());
  const cacheKey = (trener, ws) => `${trener || ""}|${ws || ""}`;
  // Save: jakmile dorazí čerstvá data, ulož do cache
  useEffect(() => {
    if (rozvrhData?.days && currentWeekMonday && activeTrener) {
      weekCacheRef.current.set(cacheKey(activeTrener, currentWeekMonday), { days: rozvrhData.days, total: rozvrhData.total || 0 });
    }
  }, [rozvrhData, currentWeekMonday, activeTrener]);
  // Prefetch: po načtení aktuálního týdne stáhneme v pozadí ±1 týden pro instant navigaci.
  // 400ms delay (snížené z 800ms) — rychlejší ale stále se skipne při rychlém přepínání.
  useEffect(() => {
    if (!activeTrener || !rozvrhData?.days) return;
    const computeWeekStart = (offset) => {
      const d = new Date(today + "T00:00:00");
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff + offset * 7);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const targetOffsets = [weekOffset + 1, weekOffset - 1].filter((o) => o >= -5 && o <= 5);
    const targets = targetOffsets
      .map((o) => ({ ws: computeWeekStart(o), ck: "" }))
      .map((t) => ({ ...t, ck: cacheKey(activeTrener, t.ws) }))
      .filter((t) => !weekCacheRef.current.has(t.ck));
    if (targets.length === 0) return;
    const timers = targets.map((t, i) =>
      setTimeout(() => {
        apiFetch(`/api/trener-rozvrh?pilot=${encodeURIComponent(activeTrener)}&weekStart=${t.ws}&days=7`)
          .then((r) => r.ok ? r.json() : null)
          .then((j) => {
            if (j?.ok && j.days) weekCacheRef.current.set(t.ck, { days: j.days, total: j.total || 0 });
          })
          .catch(() => {});
      }, 400 + i * 200)
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [rozvrhData, weekOffset, activeTrener, today]);
  // Display fallback: když API ještě nedorazilo (loading), zobraz cached (z prefetch/předchozí návštěvy)
  // POZOR: cache key musí obsahovat trenéra, jinak při přepnutí trenéra dostaneme cizí data!
  const cachedRozvrh = weekCacheRef.current.get(cacheKey(activeTrener, currentWeekMonday));
  const effectiveRozvrhData = rozvrhData || (cachedRozvrh ? { days: cachedRozvrh.days, total: cachedRozvrh.total, ok: true } : null);

  const allTrainers = trainersData?.trainers || [];
  // Najdi řádek pro activeTrener
  const myStats = useMemo(() => {
    if (!activeTrener || !allTrainers.length) return null;
    return allTrainers.find((t) => t.jmeno?.toLowerCase() === activeTrener.toLowerCase()) || null;
  }, [activeTrener, allTrainers]);

  // (today, in30Days, ago14days už definované výše před useApiData rozvrh)

  // Úkoly k řešení = vsechny otevrene (naplanovane + v_procesu), serazene podle urgence:
  // prosle → dnesni → nejblizsi deadline → bez deadlinu. Driv tu byly JEN prosle + dnesni,
  // takze trener s ukoly na pristi tyden videl "Nic v planu" a 0 — matouci.
  const ukoly = (ukolyData?.tasks || []).filter((t) => t.status === "naplanovane" || t.status === "v_procesu" || !t.status);
  const prioOrd = (t) => ({ High: 0, Medium: 1, Low: 2 }[t.priority] ?? 1);
  const overdueTasks = ukoly.filter((t) => t.deadline && t.deadline < today).sort((a, b) => prioOrd(a) - prioOrd(b));
  const todayTasks = ukoly.filter((t) => t.deadline === today).sort((a, b) => prioOrd(a) - prioOrd(b));
  const upcomingTasks = ukoly
    .filter((t) => !t.deadline || t.deadline > today)
    .sort((a, b) => String(a.deadline || "9999").localeCompare(String(b.deadline || "9999")) || (prioOrd(a) - prioOrd(b)));
  const tasksUrgent = overdueTasks.length + todayTasks.length;
  const tasksOpenTotal = ukoly.length;

  // Dokumenty — nepřečtené a aktualizované
  const docs = (docsData?.dokumenty || []).filter((d) => d.status === "unread" || d.status === "outdated");
  const docsUrgent = docs.length;

  // Klienti končící do 30 dní
  const endingClients = useMemo(() => {
    if (!myStats?.activeClients) return [];
    return myStats.activeClients
      .filter((c) => c.konec && c.konec >= today && c.konec <= in30Days)
      .sort((a, b) => a.konec.localeCompare(b.konec));
  }, [myStats, today, in30Days]);

  // Trialy čekající na rozhodnutí (pending60Names)
  const pendingTrials = myStats?.pending60Names || [];

  // Neaktivní klienti = aktivní klienti tohoto trenéra co 14+ dní nebyli na tréninku
  const neaktivniKlienti = useMemo(() => {
    if (!activeTrener || !clientsData?.clients) return [];
    const pilotLower = activeTrener.toLowerCase().trim();
    return clientsData.clients
      .filter((c) => (c.trener || "").toLowerCase().trim() === pilotLower)
      .filter((c) => c.stav === "Aktivní")
      .filter((c) => c.posledniTrenink && c.posledniTrenink < ago14days)
      .map((c) => ({
        klient: c.klient,
        posledniTrenink: c.posledniTrenink,
        telefon: c.telefon,
        email: c.email,
        balicek: c.balicek,
        daysAgo: daysBetween(c.posledniTrenink, today),
      }))
      .sort((a, b) => a.posledniTrenink.localeCompare(b.posledniTrenink)); // nejstarší první
  }, [activeTrener, clientsData, ago14days, today]);

  // Greeting
  const now = new Date();
  const greeting = HOUR_GREETING(now.getHours());
  const dayName = DAY_NAMES[now.getDay()];
  const dateLabel = `${dayName} ${now.getDate()}. ${MONTH_NAMES[now.getMonth()]}`;
  // Do pilulek v hlavičce: krátké datum a počet dnešních tréninků.
  const datumKratce = `${(dayName || "").slice(0, 2)} ${now.getDate()}.${now.getMonth() + 1}.${String(now.getFullYear()).slice(2)}`;

  const refreshAll = () => {
    refetchTrainers();
    refetchUkoly();
    refetchDocs();
    refetchClients();
    refetchRozvrh();
  };
  const isRefreshing = refreshingTrainers || refreshingUkoly || refreshingDocs || refreshingClients || refreshingRozvrh;

  // Rozvrh dnes
  // Týdenní rozvrh: data.days = [{ date, dow, bookings: [...] }]
  // Použij čerstvá data z API, fallback na cache (pro instant přepínání týdnů bez čekání)
  const rozvrhDays = effectiveRozvrhData?.days || [];
  const rozvrhTotal = effectiveRozvrhData?.total || 0;
  const dnesTreninku = (effectiveRozvrhData?.days || []).find((d) => d.date === today)?.bookings?.length || 0;

  // Pokud owner ještě nevybral trenéra
  if (isOwner && !activeTrener) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
        <Users className="w-10 h-10 text-brand-300 mx-auto mb-2" />
        <div className="text-base font-bold text-slate-700 mb-1">Vyber trenéra</div>
        <div className="text-sm text-slate-500">V hlavičce nahoře vyber kterého trenéra chceš vidět.</div>
      </div>
    );
  }

  const firstName = toVocativ(activeTrener);
  const noProblems = tasksUrgent === 0 && docsUrgent === 0 && endingClients.length === 0 && pendingTrials.length === 0 && poZkusebnimPocet === 0;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* === POZDRAV === */}
      {/* 3.9.2026: pozdrav je hlavička stránky jako všude jinde, Obnovit je ikona v ní. */}
      <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-11 sm:w-11">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-xl font-bold leading-tight sm:text-2xl">
              {greeting}, <span className="demo-sensitive">{firstName}</span>
            </h1>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold tabular-nums text-white">{datumKratce}</span>
              <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold text-white">Dnes: {dnesTreninku} {dnesTreninku === 1 ? "trénink" : dnesTreninku < 5 ? "tréninky" : "tréninků"}</span>
            </div>
          </div>
          <button
            onClick={refreshAll}
            disabled={isRefreshing}
            aria-label="Obnovit"
            title="Obnovit"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 disabled:opacity-40 sm:h-10 sm:w-10 sm:hover:bg-white/20"
          >
            <RefreshCw className={`h-[18px] w-[18px] ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* === PO ZKUŠEBNÍM: ROZHODNI (Deal u trenéra, 18. 10. 2026) ===
          Nahoře, protože je to nejnaléhavější: klientka čeká na rozhodnutí. Bez položek se nezobrazí.
          Zástup na boardu kolegy kartu nevidí (server vrací trenérovi jen jeho zkušební). */}
      {kartaPoZkusebnim && <KartaPoZkusebnim trener={isOwner ? activeTrener : null} onPocet={setPoZkusebnimPocet} />}

      {/* === HERO BOX — DNES === */}
      {/* 28.5.2026 v2: redesign — místo prostého počtu a textu se zobrazuje konkrétní
          seznam úkolů a dokumentů s deadline pill boxy. Nový úkol je integrovaný
          dashed button uvnitř úkolové kartky. Spodní 3-tlačítkový řádek odstraněn. */}
      <div className="glass min-w-0 p-4 sm:p-5">
        <h2 data-nadpis-karty className="mb-4 flex w-full min-w-0 items-center gap-3 rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-3 py-2.5 text-white shadow-[0_6px_16px_-10px_rgba(36,20,48,0.9)] sm:gap-2.5 sm:rounded-[14px] sm:px-2.5 sm:py-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20 sm:h-8 sm:w-8">
            <Sparkles className="h-5 w-5 sm:h-4 sm:w-4" />
          </span>
          <span className="min-w-0 flex-1 font-serif text-lg font-bold leading-tight">Co dnes řešíš</span>
        </h2>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {/* ÚKOLY card — seznam + Nový úkol button uvnitř */}
          <HeroTaskCard
            tasks={[...overdueTasks, ...todayTasks, ...upcomingTasks]}
            totalCount={tasksOpenTotal}
            today={today}
          />
          {/* DOKUMENTY card — seznam s deadline_dni → pill box */}
          <HeroDocCard
            docs={docs}
            totalCount={docsUrgent}
            today={today}
          />
        </div>

        {noProblems && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Vše je v klidu. Žádné akutní úkoly, dokumenty ani problémy.
          </div>
        )}
      </div>

      {/* === TÝDENNÍ ROZVRH (Po-Pá) — navigace ±5 týdnů, proklik na klient */}
      {activeTrener && (
        <PropadnutiContext.Provider value={{
          activeTrener,
          userRole: getCurrentUser()?.role || "",
          onMutated: () => {
            // Po změně propadnutí: invaliduj cache pro daný week + refetch
            try { weekCacheRef.current.delete(cacheKey(activeTrener, currentWeekMonday)); } catch {}
            refetchRozvrh?.();
          },
        }}>
          <TydenniRozvrh
            days={rozvrhDays}
            total={rozvrhTotal}
            weekOffset={weekOffset}
            onWeekOffsetChange={setWeekOffset}
            weekStart={currentWeekMonday}
            today={today}
          />
        </PropadnutiContext.Provider>
      )}

      {/* === NOTIFIKACE (sekce pod rozvrhem) — stejný feed jako zvoneček === */}
      <TrenerNotifikaceSection activeTrener={activeTrener} />

      {/* === NEAKTIVNÍ KLIENTI ===
           Blok "Vyžaduje pozornost" odstraněn 4.6.2026 — končící spolupráce i čekající
           trialy jsou nově v notifikacích/úkolech. Neaktivní klienti zůstávají jako přehled. */}
      <NeaktivniKlientiCard klienti={neaktivniKlienti} />

      {/* === PLÁNOVANÉ ABSENCE (vlastní volno trenéra) === */}
      <TrenerAbsenceCard activeTrener={activeTrener} />

      {/* === MĚSÍČNÍ SKORE === */}
      {myStats && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Target className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-800">Tvoje skore</div>
              <div className="text-[11px] text-slate-500">{myStats.lokalita}, {year}</div>
            </div>
            {/* Oko — skrytí čísel, když je trenér nechce ukázat. */}
            <button
              type="button"
              onClick={() => setScoreHidden((v) => !v)}
              title={scoreHidden ? "Zobrazit čísla" : "Skrýt čísla"}
              className="ml-auto w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
            >
              {scoreHidden ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {/* scoreHidden → rozmazat JEN čísla (.score-val), popisky/labely zůstanou čitelné. */}
          <div className={`space-y-2 sm:space-y-3 ${scoreHidden ? "[&_.score-val]:blur-md [&_.score-val]:select-none" : ""}`}>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
              <ScoreTile
                icon={Users}
                value={myStats.activeClientsCount}
                label="Aktivní klienti"
                subtitle={
                  myStats.added40d > 0 || myStats.ended40d > 0 ? (
                    <div className="inline-flex items-center gap-1 flex-wrap">
                      {myStats.added40d > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-sm font-bold tabular-nums bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                          <TrendingUp className="w-3.5 h-3.5" />{myStats.added40d}
                        </span>
                      )}
                      {myStats.ended40d > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-sm font-bold tabular-nums bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full">
                          <TrendingDown className="w-3.5 h-3.5" />{myStats.ended40d}
                        </span>
                      )}
                      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-bold ml-0.5">za 40 dní</span>
                    </div>
                  ) : null
                }
                accent="violet"
                tip="Počet klientů přiřazených tobě se stavem Aktivní. Pod ním pohyb (přírůstek / úbytek) za posledních 40 dní. Zelená šipka nahoru = noví, růžová dolů = ukončení."
              />
              <ScoreTile
                icon={Clock}
                value={myStats.avgCoopMonths ? `${myStats.avgCoopMonths} měs.` : "-"}
                label="Ø délka spolupráce"
                subtitle="vč. ukončených klientů"
                accent="sky"
                tip="Průměrná délka spolupráce všech tvých klientů (aktivních + ukončených). Počítá se podle počtu FAPI faktur klienta: Permanentka × 2 měsíce, Koučink × 1 měsíc. Trial a jednorázové se nepočítají. Počítají se jen faktury z období, kdy byl klient přiřazený tobě, nejdřív od tvého nástupu. Klienty převzaté od jiného trenéra tedy nezdědíš celou historii."
              />
              <ScoreTile
                icon={Zap}
                value={myStats.winRate60 != null ? `${myStats.winRate60} %` : "-"}
                label="Trial konverze (60d)"
                subtitle={
                  <div className="space-y-2">
                    <div className="text-sm text-slate-500 leading-snug">
                      {myStats.deals60 + myStats.losses60 > 0
                        ? `${myStats.deals60} ze ${myStats.deals60 + myStats.losses60} rozhodnutých`
                        : "za 60 dní žádné rozhodnuté"}
                    </div>
                    <div className="bg-amber-100/70 border border-amber-200 rounded-xl px-2.5 py-2">
                      <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold mb-0.5">Celá historie</div>
                      {myStats.winRateAll != null ? (
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <div className="text-xl font-black tabular-nums text-amber-700 leading-none">
                            {myStats.winRateAll} %
                          </div>
                          <div className="text-sm text-slate-600 tabular-nums">
                            {myStats.dealsAll} ze {myStats.dealsAll + myStats.lossesAll} trialů
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-slate-500">žádné rozhodnuté</div>
                      )}
                    </div>
                  </div>
                }
                accent="amber"
                tip="Procento zkušebních tréninků, ve kterých klient uzavřel spolupráci (Deal). Hlavní číslo = posledních 60 dní (akutní výkonnost). Pod tím Celá historie = všechny tvoje trialy zaznamenané v Notion/Leady. Počítají se jen trialy od tvého nástupu, trialy převzatých klientů u předchozího trenéra se nezapočítávají. Nepočítá čekající rozhodnutí (pending)."
              />
              {myStats.sazbaInfo ? (
                <PostupKVyssiSazbeCardCompact
                  sazbaInfo={myStats.sazbaInfo}
                  sazbaActual={myStats.sazba}
                  treninkyMesic={myStats.treninkyMesic}
                  avgTreninkyMesic={myStats.closedMonthsCount > 0 ? Math.round((myStats.treninkyYTDClosed || 0) / myStats.closedMonthsCount) : (myStats.treninkyMesic || 0)}
                />
              ) : (
                <ScoreTile
                  icon={DollarSign}
                  value={myStats.sazba ? `${myStats.sazba} Kč` : "-"}
                  label="Sazba"
                  subtitle="Kč / tréninková jednotka"
                  accent="emerald"
                  valueClass="text-2xl sm:text-3xl"
                  tip="Tvoje aktuální sazba dle smlouvy za jednu tréninkovou jednotku."
                />
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
              {/* TENTO MĚSÍC — fakturace kterou si vystavíš za aktuální měsíc, s tagem PROBÍHÁ */}
              <ScoreTile
                icon={Activity}
                value={myStats.wagesMesic > 0 ? `${fmtKc(myStats.wagesMesic)} Kč` : "-"}
                label="Tento měsíc"
                badge={
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wide bg-amber-500 text-white px-2.5 py-1 rounded-lg shadow-sm">
                    <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
                    PROBÍHÁ
                  </span>
                }
                subtitle={myStats.treninkyMesic > 0 ? `${myStats.treninkyMesic} tréninků nyní` : "zatím žádný trénink"}
                accent="amber"
                valueClass="text-2xl sm:text-3xl"
                tip="Aktuálně probíhající měsíc. Tvoje fakturace zatím odpovídá počtu odtrénovaných jednotek × tvoje sazba (s bonusem). Po skončení měsíce se finální částka přesune do dlaždice Fakturováno (rok)."
              />
              <ScoreTile
                icon={DollarSign}
                value={myStats.wagesAvgMonth > 0 ? `${fmtKc(myStats.wagesAvgMonth)} Kč` : "-"}
                label="Ø měsíční fakturace"
                subtitle={myStats.closedMonthsCount > 0 ? `průměr za ${myStats.closedMonthsCount} ${myStats.closedMonthsCount === 1 ? "uzavřený měsíc" : myStats.closedMonthsCount < 5 ? "uzavřené měsíce" : "uzavřených měsíců"}` : "žádný uzavřený měsíc"}
                accent="emerald"
                valueClass="text-2xl sm:text-3xl"
                tip="Tvoje průměrná měsíční fakturace = celková fakturace YTD (jen uzavřené měsíce) dělená počtem těchto měsíců. Aktuální probíhající měsíc se nezapočítává, aby průměr nebyl podstřelený. Bonus zahrnut, použila se aktuální platná sazba ze smlouvy."
              />
              <ScoreTile
                icon={TrendingUp}
                value={myStats.wagesYTDClosed > 0 ? `${fmtKc(myStats.wagesYTDClosed)} Kč` : "-"}
                label={`Fakturováno ${year}`}
                subtitle={myStats.treninkyYTDClosed > 0 ? `${myStats.treninkyYTDClosed} tréninků v ${myStats.closedMonthsCount} uzavřených měs.` : "do posledního uzavřeného měsíce"}
                accent="emerald"
                valueClass="text-2xl sm:text-3xl"
                tip={`Celková fakturace za uzavřené měsíce roku ${year} (bez aktuálně probíhajícího). Pro každý měsíc se použila sazba platná k poslednímu dni měsíce, takže respektuje navýšení po milnících. Probíhající měsíc se přidá až po jeho skončení.`}
              />
              <ScoreTile
                icon={Award}
                value={myStats.wagesYTD > 0 ? `${fmtKc(myStats.wagesYTD)} Kč` : "-"}
                label={`Celkem od ${year}`}
                subtitle={myStats.treninkyYTD > 0 ? `${myStats.treninkyYTD} tréninků celkem` : null}
                accent="amber"
                valueClass="text-2xl sm:text-3xl"
                tip={`Celková fakturace od 1. 1. ${year} včetně aktuálního probíhajícího měsíce. Toto číslo poroste s každým novým tréninkem do konce měsíce. Pro „uzavřená" finální čísla se podívej na dlaždici Fakturováno ${year}.`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Neaktivní klienti přesunuty nahoru vedle Vyžaduje pozornost.
          Postup k sazbě se přesunul do hlavního skore gridu jako compact dlaždice. */}

    </div>
  );
}

// === ROZVRH — list tréninků pro vybraný den (Cache bookings 2026) s day-pickerem ±2 dny ===
// === TÝDENNÍ ROZVRH — Po-Pá vždy, víkend (So+Ne) JEN pokud mají bookings.
// Backend vrátí 7 dnů, frontend filtruje. Šipky ±5 týdnů, klient klikatelný.
const DAY_NAMES_FULL = ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"];

export function TydenniRozvrh({ days, total, weekOffset, onWeekOffsetChange, weekStart, today }) {
  // Zobrazit i prázdné pracovní dny (default ne).
  const [showEmptyDays, setShowEmptyDays] = useState(false);
  // Který den je rozbalený. null = drž se dneška (nebo prvního dne v týdnu).
  const [otevrenyDen, setOtevrenyDen] = useState(null);

  // Víkend jen s tréninky, pracovní den bez tréninků jen na vyžádání nebo když je dnes.
  const visibleDays = useMemo(() => {
    return (days || []).filter((d) => {
      const isWeekend = d.dow === 0 || d.dow === 6;
      if (isWeekend) return d.bookings.length > 0;
      if (showEmptyDays) return true;
      return d.bookings.length > 0 || d.date === today;
    });
  }, [days, showEmptyDays, today]);

  const hiddenEmptyCount = useMemo(() => {
    return (days || []).filter((d) => {
      const isWeekend = d.dow === 0 || d.dow === 6;
      if (isWeekend) return false;
      if (d.date === today) return false;
      return d.bookings.length === 0;
    }).length;
  }, [days, today]);

  const fmtCzDate = (iso) => {
    if (!iso) return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}.` : iso;
  };
  const weekRangeShort = useMemo(() => {
    if (!days || days.length === 0) return "-";
    const first = days[0]?.date;
    const last = days[days.length - 1]?.date;
    if (!first || !last) return "-";
    return `${fmtCzDate(first)} až ${fmtCzDate(last)}`;
  }, [days]);

  const isCurrent = weekOffset === 0;
  const minOffset = -5;
  const maxOffset = 5;

  // Který den je otevřený: vybraný, jinak dnešek, jinak první viditelný.
  const aktivniDen = useMemo(() => {
    if (otevrenyDen && visibleDays.some((d) => d.date === otevrenyDen)) return otevrenyDen;
    if (visibleDays.some((d) => d.date === today)) return today;
    return visibleDays[0]?.date || null;
  }, [otevrenyDen, visibleDays, today]);

  // Ráno je do 12:00 včetně 11:xx, zbytek odpoledne. Trénink bez času jde do rána.
  const rozdelDne = (bookings) => {
    const skupiny = groupBookingsByTime(bookings);
    const rano = skupiny.filter((g) => timeToMinutes(g.cas) < 12 * 60);
    const odpoledne = skupiny.filter((g) => timeToMinutes(g.cas) >= 12 * 60);
    return { rano, odpoledne };
  };

  const pocetPodleTypu = (bookings) => {
    const skupiny = groupBookingsByTime(bookings);
    const { rano, odpoledne } = rozdelDne(bookings);
    const spojene = skupiny.filter((g) => g.klienti.length > 1).length;
    return { rano: rano.length, odpoledne: odpoledne.length, spojene, celkem: skupiny.length };
  };

  return (
    <div id="rozvrh" className="glass min-w-0 scroll-mt-4 p-4 sm:p-5">
      {/* Lišta karty s listováním týdnem */}
      <h2 data-nadpis-karty className="mb-3 flex w-full min-w-0 items-center gap-3 rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-3 py-2.5 text-white shadow-[0_6px_16px_-10px_rgba(36,20,48,0.9)] sm:gap-2.5 sm:rounded-[14px] sm:px-2.5 sm:py-2">
        <button
          onClick={() => onWeekOffsetChange(weekOffset - 1)}
          disabled={weekOffset <= minOffset}
          className="btn-mesic"
          aria-label="Předchozí týden"
          title="Předchozí týden"
        >
          <ArrowRight className="h-5 w-5 rotate-180 sm:h-4 sm:w-4" />
        </button>
        <span className="min-w-0 flex-1 text-center">
          <span className="block truncate text-[16.5px] font-bold leading-tight tracking-tight sm:text-[14.5px]">{weekRangeShort}</span>
        </span>
        <span className="hidden shrink-0 rounded-xl bg-white/20 px-2.5 py-1 text-xs font-bold tabular-nums text-white sm:inline-block">
          {total} {total === 1 ? "trénink" : total < 5 ? "tréninky" : "tréninků"}
        </span>
        <button
          onClick={() => onWeekOffsetChange(weekOffset + 1)}
          disabled={weekOffset >= maxOffset}
          className="btn-mesic"
          aria-label="Následující týden"
          title="Následující týden"
        >
          <ArrowRight className="h-5 w-5 sm:h-4 sm:w-4" />
        </button>
      </h2>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800 sm:hidden">
          {total} {total === 1 ? "trénink" : total < 5 ? "tréninky" : "tréninků"}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {(hiddenEmptyCount > 0 || showEmptyDays) && (
            <button
              onClick={() => setShowEmptyDays((x) => !x)}
              className="btn btn-secondary btn-sm"
              title={showEmptyDays ? "Skrýt dny bez tréninků" : `Zobrazit ${hiddenEmptyCount} dnů bez tréninků`}
            >
              {showEmptyDays ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showEmptyDays ? "Skrýt prázdné" : `Prázdné dny (${hiddenEmptyCount})`}
            </button>
          )}
          {!isCurrent && (
            <button onClick={() => onWeekOffsetChange(0)} className="btn btn-secondary btn-sm">Na tento týden</button>
          )}
        </div>
      </div>

      {/* Dny pod sebou. Otevřený den se rozbalí přímo pod svým řádkem. */}
      <div className="space-y-2">
        {visibleDays.map((d) => {
          const isToday = d.date === today;
          const otevreno = d.date === aktivniDen;
          const dayName = DAY_NAMES_FULL[d.dow] || "";
          const p = pocetPodleTypu(d.bookings);
          const { rano, odpoledne } = rozdelDne(d.bookings);
          const zaklad = isToday ? "bg-brand-200 text-brand-900" : "bg-slate-100 text-slate-700";
          return (
            <div key={d.date}>
              <button
                onClick={() => setOtevrenyDen(otevreno ? "" : d.date)}
                aria-expanded={otevreno}
                className={`flex min-h-[64px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left ${
                  isToday ? "border-2 border-brand bg-brand-50" : "border border-slate-200 bg-white"
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-bold ${isToday ? "text-brand-deep" : "text-slate-900"}`}>
                    {isToday ? `Dnes, ${dayName.toLowerCase()} ${fmtCzDate(d.date)}` : `${dayName} ${fmtCzDate(d.date)}`}
                  </span>
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    {p.celkem === 0 ? (
                      <span className="inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">Bez tréninků</span>
                    ) : (
                      <>
                        {p.rano > 0 && <span className={`inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold tabular-nums ${zaklad}`}>{p.rano} ráno</span>}
                        {p.odpoledne > 0 && <span className={`inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold tabular-nums ${zaklad}`}>{p.odpoledne} odpoledne</span>}
                        {p.spojene > 0 && <span className="inline-flex items-center rounded-lg bg-emerald-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-emerald-800">{p.spojene} spojený</span>}
                      </>
                    )}
                  </span>
                </span>
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold tabular-nums ${isToday ? "bg-brand text-white" : "bg-slate-100 text-slate-700"}`}>
                  {p.celkem}
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${otevreno ? "rotate-180" : ""} ${isToday ? "text-brand-deep" : "text-slate-400"}`} />
              </button>

              {otevreno && p.celkem > 0 && (
                <div className="mt-2 rounded-xl border border-brand/15 bg-brand-50/60 p-3">
                  {rano.length > 0 && (
                    <>
                      <div className="mb-2 flex items-center gap-2.5 rounded-xl bg-brand-100 px-3 py-2">
                        <Sunrise className="h-4 w-4 shrink-0 text-brand-deep" />
                        <span className="min-w-0 flex-1 text-sm font-bold text-brand-950">Ráno</span>
                        <span className="shrink-0 text-sm font-bold tabular-nums text-brand-800">{rano.length}</span>
                      </div>
                      <div className="mb-3 space-y-1.5">
                        {rano.map((g, i) => (
                          <BookingChip key={`${d.date}-r-${g.cas || i}-${g.klienti.map((k) => k.klient).join("|")}`} group={g} date={d.date} />
                        ))}
                      </div>
                    </>
                  )}
                  {odpoledne.length > 0 && (
                    <>
                      <div className="mb-2 flex items-center gap-2.5 rounded-xl bg-plum-100 px-3 py-2">
                        <Sunset className="h-4 w-4 shrink-0 text-plum-700" />
                        <span className="min-w-0 flex-1 text-sm font-bold text-plum-900">Odpoledne</span>
                        <span className="shrink-0 text-sm font-bold tabular-nums text-plum-700">{odpoledne.length}</span>
                      </div>
                      <div className="space-y-1.5">
                        {odpoledne.map((g, i) => (
                          <BookingChip key={`${d.date}-o-${g.cas || i}-${g.klienti.map((k) => k.klient).join("|")}`} group={g} date={d.date} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {total === 0 && (
        <p className="mt-3 text-center text-sm text-slate-500">Tento týden nemáš žádné potvrzené tréninky.</p>
      )}
    </div>
  );
}

// === BOOKING GROUPING — sloučí dva tréninky se stejným časem (spojený trénink) ===
function groupBookingsByTime(bookings) {
  const map = new Map();
  (bookings || []).forEach((b, idx) => {
    const cas = (b.cas || "").trim();
    if (!cas) {
      // Bez času = vlastní řádek (nesloučit)
      map.set(`__notime_${idx}`, { cas: "", casKonec: "", klienti: [b], _order: 100000 + idx });
      return;
    }
    if (!map.has(cas)) {
      // casKonec ze stejného slotu (spojené tréninky mají stejný konec) — bere se z prvního.
      map.set(cas, { cas, casKonec: (b.casKonec || "").trim(), klienti: [], _order: timeToMinutes(cas) });
    }
    map.get(cas).klienti.push(b);
  });
  // Seřadit podle času (no-time na konec)
  return Array.from(map.values()).sort((a, b) => a._order - b._order);
}

function timeToMinutes(cas) {
  const m = String(cas || "").match(/^(\d{1,2}):(\d{2})/);
  if (!m) return 9999;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

// === PROPADNUTÍ KONTEXT — sdílí activeTrener + onMutated callback do BookingChip ===
export const PropadnutiContext = createContext({ activeTrener: "", userRole: "", onMutated: () => {} });

// === MODAL pro označení / vrácení propadnutí ===
// Optimistic UX: klik na confirm → ihned zavřeme modal a překlopíme UI přes onOptimisticSubmit.
// Fetch běží v BookingClientRow v pozadí, žádné čekání tady.
function PropadnutiModal({ open, onClose, booking, currentDate, activeTrener, onOptimisticSubmit }) {
  if (!open || !booking) return null;
  const isPropadly = !!booking.propadnuti;

  const handleSubmit = () => {
    // Okamžitě překlop stav a zavři modal — fetch backend běží v pozadí v parent komponentě
    onOptimisticSubmit?.(!isPropadly);
  };

  return (
    <div className="fixed inset-0 bg-brand-dark/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className={`p-5 border-b border-slate-200 flex items-center gap-3 ${isPropadly ? "bg-emerald-50" : "bg-rose-50"}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPropadly ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
            {isPropadly ? <RotateCcw className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          </div>
          <div>
            <div className="text-base font-black text-slate-900">
              {isPropadly ? "Vrátit propadnutí?" : "Označit trénink jako propadlý?"}
            </div>
            <div className="text-xs text-slate-600">
              {isPropadly
                ? "Trénink přestane být označen jako propadlý."
                : "Klient zrušil méně než 24 h dopředu, trénink propadl."}
            </div>
          </div>
        </div>

        <div className="p-5 space-y-2 text-sm">
          <div className="flex justify-between items-baseline">
            <span className="text-slate-500">Klient:</span>
            <span className="font-bold text-slate-900 demo-sensitive">{booking.klient}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-slate-500">Datum a čas:</span>
            <span className="font-bold text-slate-900 tabular-nums">{currentDate}, {booking.cas}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-slate-500">Trenér:</span>
            <span className="font-semibold text-slate-700">{activeTrener}</span>
          </div>
          {!isPropadly && (
            <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
              <b>Upozornění:</b> Propadlý trénink se klasicky strhne, jako by proběhl.
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="text-sm text-slate-600 hover:text-slate-900 font-semibold px-4 py-2 rounded-xl hover:bg-slate-100 min-h-[44px] sm:min-h-0">
            Zrušit
          </button>
          <button onClick={handleSubmit}
            className={`inline-flex items-center gap-2 font-bold px-4 py-2 rounded-xl text-sm shadow-sm text-white ${
              isPropadly ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
            }`}>
            {isPropadly ? <RotateCcw className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {isPropadly ? "Vrátit zpět" : "Ano, trénink propadl"}
          </button>
        </div>
      </div>
    </div>
  );
}

// === BOOKING CHIP — jeden časový slot (1 klient = osobní, 2+ = spojený trénink) ===
function BookingChip({ group, date }) {
  const { cas, casKonec, klienti } = group;
  const isGroup = klienti.length > 1;
  // Čas od–do (např. "19:00–20:00"); když konec chybí (starší data před full syncem), jen začátek.
  const timeLabel = cas ? (casKonec ? `${cas} až ${casKonec}` : cas) : "-";

  // Barevná paleta: solo violet, spojený emerald
  const palette = isGroup
    ? {
        bg: "bg-emerald-50",
        border: "border-emerald-300",
        stitek: "bg-emerald-200 text-emerald-900",
        timeColor: "text-emerald-700",
        labelColor: "text-emerald-800",
        nameHover: "hover:text-emerald-900 hover:underline",
        bullet: "text-emerald-600",
      }
    : {
        bg: "bg-white",
        border: "border-brand-200",
        stitek: "bg-brand-100 text-brand-900",
        timeColor: "text-brand-700",
        labelColor: "text-brand-800",
        nameHover: "hover:text-brand-800 hover:underline",
        bullet: "text-brand-600",
      };

  // Štítek typu: "Osobní trénink" / "Spojený trénink (2)"
  const typeLabel = isGroup ? `Spojený, ${klienti.length} klientky` : "Osobní";

  return (
    <div className={`rounded-xl border ${palette.border} ${palette.bg} px-3 py-2`}>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold tabular-nums ${palette.timeColor}`}>{timeLabel}</span>
        <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${palette.stitek}`}>{typeLabel}</span>
      </div>
      <div className="space-y-1">
        {klienti.map((k, i) => {
          const klientLink = `/trener/klienti?q=${encodeURIComponent(k.klient || "")}`;
          // Cizí klient: oficiální coach v Stav 2026 je jiný trenér. Trenér ten trénink REÁLNĚ
          // vedl (zaskakování) → ukáž žlutý badge + tooltip s jménem oficiálního coache.
          const isCizi = !!k.isCizi;
          const oficiCoach = k.pilotName || "";
          // Štítky per klient — z backendu k.pills = [{ kind, text, severity }]
          // severity → barva pillu (danger=červená, warning=amber, info=modrá, success=zelená, loyalty=fialová)
          const pillStyle = (sev) => {
            switch (sev) {
              case "danger": return "bg-rose-100 text-rose-800 border-rose-200";
              case "warning": return "bg-amber-100 text-amber-800 border-amber-200";
              case "success": return "bg-emerald-100 text-emerald-800 border-emerald-200";
              case "info": return "bg-sky-100 text-sky-800 border-sky-200";
              case "loyalty": return "bg-brand-100 text-brand-800 border-brand-200";
              default: return "bg-slate-100 text-slate-700 border-slate-200";
            }
          };
          // Sort pillů: nejdřív danger, pak warning, info, success, loyalty (podle závažnosti)
          const sevOrder = { danger: 0, warning: 1, info: 2, success: 3, loyalty: 4 };
          const pills = [...(k.pills || [])].sort((a, b) => (sevOrder[a.severity] ?? 99) - (sevOrder[b.severity] ?? 99));
          const isPropadly = !!k.propadnuti;
          return (
            <BookingClientRow
              key={i}
              k={k}
              date={date}
              cas={cas}
              isGroup={isGroup}
              isCizi={isCizi}
              oficiCoach={oficiCoach}
              klientLink={klientLink}
              palette={palette}
              pills={pills}
              pillStyle={pillStyle}
              isPropadly={isPropadly}
            />
          );
        })}
      </div>
    </div>
  );
}

// === ŘÁDEK KLIENTA V BOOKING CHIPU — s tlačítkem propadnutí + modálem ===
// Optimistic update: klik na confirm v modálu → ihned přepneme localPropadly → modal zmizí.
// Fetch backend běží v pozadí. Pokud selže, vrátíme stav + error toast.
function BookingClientRow({ k, date, cas, isGroup, isCizi, oficiCoach, klientLink, palette, pills, pillStyle, isPropadly: serverPropadly }) {
  const ctx = useContext(PropadnutiContext);
  const [modalOpen, setModalOpen] = useState(false);
  // Lokální optimistic override — null = používáme server hodnotu, true/false = uživatel právě toggle
  const [localOverride, setLocalOverride] = useState(null);
  // KRITICKÉ: reset localOverride když se změní identita klienta/datumu/trenéra nebo když dorazí nová server hodnota.
  // Bez tohoto by visual stav z minulého klienta zůstal viset na novém klientovi (např. přepnutí trenéra).
  useEffect(() => {
    setLocalOverride(null);
  }, [serverPropadly, k.klient, date, cas, ctx.activeTrener]);
  const isPropadly = localOverride !== null ? localOverride : serverPropadly;
  const allowedRoles = ["owner", "manager", "trener"];
  const canEdit = allowedRoles.includes((ctx.userRole || "").toLowerCase());

  // Optimistic submit — okamžitě překlopíme UI, fetch běží v pozadí
  const handleOptimisticSubmit = (newValue) => {
    setLocalOverride(newValue);
    setModalOpen(false);
    // Background fetch
    apiFetch("/api/booking-propadnuti", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-fw40-role": (getCurrentUser()?.role || "").toLowerCase(),
      },
      body: JSON.stringify({
        date, cas, klient: k.klient, trener: ctx.activeTrener,
        propadnuti: newValue ? "ano" : "",
      }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok || j.error) {
          // Revert + log
          console.error("[propadnuti] backend error:", j.error);
          setLocalOverride(null);
          toast(`Chyba uložení propadnutí: ${j.error || "neznámá"}`, "error");
        }
        // Po úspěchu necháme localOverride aktivní (server bude konzistentní při příštím fetch)
      })
      .catch((e) => {
        console.error("[propadnuti] network error:", e);
        setLocalOverride(null);
        toast("Síťová chyba při ukládání propadnutí.", "error");
      });
  };

  // Propadlý řádek je výrazně růžový, ne jen průhlednější.
  const rowClass = isPropadly ? "-mx-1 rounded-lg bg-rose-100/70 px-1 py-1" : "";

  return (
    <div className={rowClass}>
      {/* Jméno je proklik na klientku, křížek vedle něj označí trénink jako propadlý. */}
      <div className="flex items-center gap-2">
        {isGroup && <span className={`${palette.bullet} shrink-0 text-base leading-none`}>•</span>}
        <Link
          to={klientLink}
          className={`demo-sensitive min-w-0 flex-1 truncate text-sm font-semibold underline decoration-brand-300 underline-offset-2 transition-colors ${
            isPropadly ? "text-rose-700 line-through decoration-rose-300" : "text-brand-800"
          }`}
          title={isCizi
            ? `Cizí klientka, oficiální coach ${oficiCoach || "?"}. Otevřít profil: ${k.klient}${k.balicek ? `, ${k.balicek}` : ""}`
            : `Otevřít profil klientky: ${k.klient}${k.balicek ? `, ${k.balicek}` : ""}`}
        >
          {k.klient}
        </Link>
        {canEdit && (
          <button
            onClick={() => setModalOpen(true)}
            className={`btn-ikona shrink-0 ${isPropadly ? "text-emerald-600" : "text-slate-400"}`}
            title={isPropadly ? "Vrátit propadnutí" : "Označit jako propadlý (klientka zrušila méně než 24 hodin předem)"}
            aria-label={isPropadly ? "Vrátit propadnutí" : "Označit jako propadlý"}
          >
            {isPropadly ? <RotateCcw className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
          </button>
        )}
      </div>

      {/* Štítky: propadl, cizí klientka, balíček a upozornění z backendu. */}
      {(isPropadly || isCizi || pills.length > 0) && (
        <div className={`mt-1 flex flex-wrap gap-1 ${isPropadly ? "opacity-70" : ""}`}>
          {isPropadly && (
            <span className="inline-flex items-center rounded border border-rose-300 bg-rose-200 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-rose-900">
              Propadl
            </span>
          )}
          {isCizi && (
            <span
              className="inline-flex items-center rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800"
              title={`Cizí klientka. Oficiální coach: ${oficiCoach || "?"}`}
            >
              Cizí{oficiCoach ? `, ${oficiCoach}` : ""}
            </span>
          )}
          {pills.map((p, pi) => (
            <span
              key={pi}
              className={`inline-flex max-w-full items-center truncate rounded border px-1.5 py-0.5 text-[11px] font-semibold ${pillStyle(p.severity)}`}
              title={p.text}
            >
              {p.text}
            </span>
          ))}
        </div>
      )}

      <PropadnutiModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        booking={{ klient: k.klient, cas, propadnuti: isPropadly }}
        currentDate={date}
        activeTrener={ctx.activeTrener}
        onOptimisticSubmit={handleOptimisticSubmit}
      />
    </div>
  );
}



// === Formátování data → pill text + barva ===
// Pro úkoly (deadline) + dokumenty (computed deadline z updated_at + deadline_dni).
function formatDeadlinePill(iso, today) {
  if (!iso) return { text: "-", tone: "neutral" };
  if (iso < today) {
    const days = Math.round((new Date(today + "T00:00:00") - new Date(iso + "T00:00:00")) / 86400000);
    return {
      text: days === 1 ? "1 den po deadline" : `${days} dní po deadline`,
      tone: "danger",
    };
  }
  if (iso === today) return { text: "Dnes", tone: "warning" };
  const tomorrow = new Date(today + "T00:00:00");
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (iso === tomorrow.toISOString().substring(0, 10)) return { text: "Zítra", tone: "info" };
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { text: iso, tone: "neutral" };
  return { text: `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.`, tone: "neutral" };
}

// 3.9.2026: hero karty jsou světlé, proto i pilulky termínu jedou na světlém podkladu.
const PILL_STYLE = {
  danger:  "border border-rose-200 bg-rose-100 text-rose-800",
  warning: "border border-amber-200 bg-amber-100 text-amber-800",
  info:    "border border-sky-200 bg-sky-100 text-sky-800",
  neutral: "border border-slate-200 bg-slate-100 text-slate-700",
};

function DeadlinePill({ iso, today, prefix = "" }) {
  const { text, tone } = formatDeadlinePill(iso, today);
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full tabular-nums whitespace-nowrap ${PILL_STYLE[tone]}`}>
      {prefix}{text}
    </span>
  );
}

// === ÚKOLY karta v hero boxu ===
// Header (ikona + nadpis) = klik na /trener/ukoly (otevřít vše).
// Položky: jméno + pill deadline; klik = také na /trener/ukoly (kanban view, scroll na úkol).
// Empty state: drobná hláška + dashed button Nový úkol zůstává.
function HeroTaskCard({ tasks, totalCount, today }) {
  const visible = tasks.slice(0, 4);
  const more = Math.max(0, totalCount - visible.length);
  const isEmpty = visible.length === 0;
  return (
    <div className="polozka flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <Link
          to="/trener/ukoly"
          className="inline-flex min-h-[44px] items-center gap-2 px-2 py-1 -mx-1 rounded-xl transition-colors sm:min-h-0"
          title="Otevřít všechny úkoly"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-light text-brand-deep">
            <ListChecks className="h-4 w-4" />
          </div>
          <span className="text-sm font-bold text-slate-900">Úkoly k řešení</span>
        </Link>
        <span className="rounded-full bg-brand-light px-2.5 py-0.5 text-sm font-bold tabular-nums text-brand-deep">
          {totalCount}
        </span>
      </div>

      {/* List nebo empty */}
      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center py-4 text-sm italic text-slate-500">
          Žádné otevřené úkoly
        </div>
      ) : (
        <div className="flex-1 space-y-1.5 mb-3">
          {visible.map((t) => (
            <Link
              key={t.id || t.nazev}
              to={t.id ? `/trener/ukoly?open=${encodeURIComponent(t.id)}` : "/trener/ukoly"}
              className="flex items-center justify-between gap-2 min-h-[48px] rounded-xl border border-slate-200 bg-white px-2.5 py-2 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  t.deadline && t.deadline < today
                    ? "bg-rose-500"
                    : t.deadline === today ? "bg-amber-500" : "bg-slate-300"
                }`} />
                <span className="truncate text-sm font-semibold text-slate-900">{t.nazev}</span>
              </div>
              {/* Priorita — barevny pill (High cervena, Medium amber, Low neutralni) */}
              {t.priority && t.priority !== "Medium" && (
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${
                  t.priority === "High"
                    ? "border border-rose-200 bg-rose-100 text-rose-800"
                    : "border border-slate-200 bg-slate-100 text-slate-600"
                }`}>
                  {t.priority === "High" ? "Vysoká" : "Nízká"}
                </span>
              )}
              <DeadlinePill iso={t.deadline} today={today} />
            </Link>
          ))}
          {more > 0 && (
            <div className="pt-1 text-center text-[11px] italic text-slate-500">
              +{more} {more === 1 ? "další" : more < 5 ? "další" : "dalších"}
            </div>
          )}
        </div>
      )}

      {/* Nový úkol - query param ?new=1 dá pokyn /trener/ukoly aby auto-vytvořila draft a otevřela detail */}
      <Link
        to="/trener/ukoly?new=1"
        className="mt-auto w-full flex items-center justify-center gap-1.5 min-h-[48px] rounded-xl border border-dashed border-brand/40 text-sm font-semibold text-brand-deep transition-colors"
      >
        <Plus className="w-4 h-4" />
        Nový úkol
      </Link>
    </div>
  );
}

// === DOKUMENTY karta v hero boxu ===
function HeroDocCard({ docs, totalCount, today }) {
  const visible = docs.slice(0, 4);
  const more = Math.max(0, totalCount - visible.length);
  const isEmpty = visible.length === 0;

  // Deadline = updated_at + deadline_dni (kdy musí být přečteno/podepsáno).
  const computeDocDeadline = (d) => {
    const base = d.updated_at || d.created_at;
    if (!base || !d.deadline_dni) return null;
    const dt = new Date(base);
    if (isNaN(dt.getTime())) return null;
    dt.setDate(dt.getDate() + parseInt(d.deadline_dni, 10));
    return dt.toISOString().substring(0, 10);
  };

  return (
    <div className="polozka flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <Link
          to="/trener/dokumenty"
          className="inline-flex min-h-[44px] items-center gap-2 px-2 py-1 -mx-1 rounded-xl transition-colors sm:min-h-0"
          title="Otevřít všechny dokumenty"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-light text-brand-deep">
            <BookOpen className="h-4 w-4" />
          </div>
          <span className="text-sm font-bold text-slate-900">K přečtení</span>
        </Link>
        <span className="rounded-full bg-brand-light px-2.5 py-0.5 text-sm font-bold tabular-nums text-brand-deep">
          {totalCount}
        </span>
      </div>

      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center py-4 text-sm italic text-slate-500">
          Vše přečteno
        </div>
      ) : (
        <div className="flex-1 space-y-1.5">
          {visible.map((d) => {
            const deadline = computeDocDeadline(d);
            return (
              <Link
                key={d.slug || d.nazev}
                to={`/trener/dokumenty/${encodeURIComponent(d.slug || "")}`}
                className="flex items-center justify-between gap-2 min-h-[48px] rounded-xl border border-slate-200 bg-white px-2.5 py-2 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${d.status === "outdated" ? "bg-orange-300" : "bg-amber-500"}`} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{d.nazev}</div>
                    <div className={`text-[11px] uppercase font-bold tracking-wider ${d.status === "outdated" ? "text-orange-700" : "text-slate-500"}`}>
                      {d.status === "outdated" ? "Aktualizováno" : "K podpisu"}
                    </div>
                  </div>
                </div>
                {deadline && <DeadlinePill iso={deadline} today={today} prefix="do " />}
              </Link>
            );
          })}
          {more > 0 && (
            <div className="pt-1 text-center text-[11px] italic text-slate-500">
              +{more} {more === 1 ? "další" : more < 5 ? "další" : "dalších"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// === SCORE TILE — měsíční metrika ===
function ScoreTile({ icon: Icon, value, label, subtitle, accent = "slate", valueClass = "", tip = null, badge = null }) {
  const accentMap = {
    violet:  { bg: "bg-brand-50",  border: "border-brand-200",  iconBg: "bg-brand-100  text-brand-700"  },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", iconBg: "bg-emerald-100 text-emerald-700" },
    amber:   { bg: "bg-amber-50",   border: "border-amber-200",   iconBg: "bg-amber-100   text-amber-700"   },
    sky:     { bg: "bg-sky-50",     border: "border-sky-200",     iconBg: "bg-sky-100     text-sky-700"     },
    slate:   { bg: "bg-slate-50",   border: "border-slate-200",   iconBg: "bg-slate-100   text-slate-700"   },
  };
  const a = accentMap[accent];
  return (
    <div className={`${a.bg} border ${a.border} rounded-xl p-3.5 sm:p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-600 font-bold leading-tight flex-1 min-w-0 inline-flex items-center gap-1.5 flex-wrap">
          <span className="truncate">{label}</span>
          {badge}
          {tip && <InfoTip text={tip} />}
        </div>
      </div>
      <div className={`score-val font-black text-slate-900 tabular-nums leading-tight demo-sensitive ${valueClass || "text-2xl sm:text-3xl"}`}>{value}</div>
      {subtitle && <div className="score-val text-sm text-slate-500 mt-1.5 leading-snug">{subtitle}</div>}
    </div>
  );
}


// === NEAKTIVNÍ KLIENTI — list klientů 14+ dní bez tréninku, akce: ozvat se ===
function NeaktivniKlientiCard({ klienti }) {
  const isEmpty = !klienti || klienti.length === 0;
  const top = (klienti || []).slice(0, 5);
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 sm:px-5 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
              Neaktivní klienti
              <InfoTip text="Tvoji aktivní klienti, kteří 14+ dní nebyli na tréninku. Datum posledního tréninku je údaj Poslední trénink v kartě klienta. Doporučujeme se ozvat, udržení klienta = peníze." />
            </div>
            <div className="text-xs text-slate-500">
              {isEmpty ? "Všichni aktivní mají nedávný trénink" : `${klienti.length} ${klienti.length === 1 ? "klient" : klienti.length < 5 ? "klienti" : "klientů"} 14+ dní bez tréninku`}
            </div>
          </div>
        </div>
        {!isEmpty && (
          <span className="text-xl font-black tabular-nums text-amber-600">{klienti.length}</span>
        )}
      </div>
      {isEmpty ? (
        <div className="px-4 py-8 text-center text-sm text-emerald-600 inline-flex items-center justify-center gap-2 w-full">
          <CheckCircle2 className="w-4 h-4" />
          Vše v pohodě, žádný klient delší dobu nechyběl.
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {top.map((c, i) => (
              <NeaktivniKlientRow key={i} klient={c} />
            ))}
          </div>
          {klienti.length > top.length && (
            <div className="px-4 py-2 text-xs text-slate-500 text-center bg-slate-50 border-t border-slate-100">
              + {klienti.length - top.length} dalších v <Link to="/trener/klienti" className="text-brand-600 hover:underline font-semibold">seznamu klientů</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NeaktivniKlientRow({ klient }) {
  const { klient: jmeno, daysAgo, telefon, email, balicek } = klient;
  const urgent = daysAgo >= 21;
  return (
    <div className="px-4 sm:px-5 py-2.5 flex items-center gap-3 hover:bg-amber-50/30">
      <div className={`w-9 h-9 rounded-full ${urgent ? "bg-red-500" : "bg-amber-500"} text-white font-black flex items-center justify-center text-sm flex-shrink-0`}>
        {daysAgo}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-slate-900 truncate demo-sensitive">
          {jmeno}
          {urgent && <span className="ml-1.5 text-[11px] uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">URGENT</span>}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {daysAgo} {daysAgo === 1 ? "den" : daysAgo < 5 ? "dny" : "dní"} bez tréninku
          {balicek && <span className="text-slate-400">, {balicek}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {telefon && (
          <a
            href={`tel:${telefon}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors"
            title={`Zavolat ${telefon}`}
          >
            <span className="text-xs font-bold">📞</span>
          </a>
        )}
        {email && (
          <a
            href={`mailto:${email}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-brand-100 hover:bg-brand-200 text-brand-700 transition-colors"
            title={`Email ${email}`}
          >
            <span className="text-xs font-bold">✉</span>
          </a>
        )}
      </div>
    </div>
  );
}

// === POSTUP K VYŠŠÍ SAZBĚ — motivační karta s tier countdown ===
// Kompaktní varianta PostupKVyssiSazbeCard ve velikosti ScoreTile —
// pro umístění do hlavního skóre gridu (4 dlaždice v řadě).
// Místo dvou velkých boxů zobrazí jednu velkou cifru (aktuální sazba) +
// inline sub s šipkou na příští sazbu a countdownem.
function PostupKVyssiSazbeCardCompact({ sazbaInfo, sazbaActual, treninkyMesic, avgTreninkyMesic }) {
  const milnik = sazbaInfo.pristiMilnik;
  const fmtCzDate = (iso) => {
    if (!iso) return "-";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}` : iso;
  };
  const isVyjimka = sazbaInfo.source === "vyjimka_hardcoded" || sazbaInfo.source === "vyjimka_explicitni";

  // Smluvní výjimka — pevná individuální sazba, žádné milníky
  if (isVyjimka) {
    return (
      <div className="bg-brand-50 border border-brand-200 rounded-xl p-3.5 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center flex-shrink-0">
            <Award className="w-4 h-4" />
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-600 font-bold leading-tight flex-1 min-w-0 inline-flex items-center gap-1.5 flex-wrap">
            <span className="truncate">Sazba (výjimka)</span>
            <InfoTip text="Tvoje sazba je mimo standardní vzorec (Příloha A.2 smlouvy), individuální dohoda." />
          </div>
        </div>
        <div className="font-black text-brand-700 tabular-nums leading-tight text-2xl sm:text-3xl">
          {sazbaActual} <span className="text-base font-bold">Kč/jed.</span>
        </div>
        {sazbaInfo.varovani && (
          <div className="text-sm text-slate-600 mt-1.5 leading-snug line-clamp-2">{sazbaInfo.varovani}</div>
        )}
      </div>
    );
  }

  // Maximální sazba dosažená
  if (!milnik) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Award className="w-4 h-4" />
          </div>
          <div className="text-xs uppercase tracking-wider text-slate-600 font-bold leading-tight flex-1 min-w-0 inline-flex items-center gap-1.5 flex-wrap">
            <span className="truncate">Sazba</span>
            <InfoTip text="Jsi v nejvyšší sazbě smlouvy (po 36 měsících spolupráce). Sazba se už dál nezvyšuje." />
          </div>
        </div>
        <div className="score-val font-black text-amber-700 tabular-nums leading-tight text-2xl sm:text-3xl">
          {sazbaActual} <span className="text-base font-bold">Kč/jed.</span>
        </div>
        <div className="text-sm text-slate-600 mt-1.5">Maximální sazba dosažena.</div>
      </div>
    );
  }

  // Standardní postup: aktuální + countdown
  const today = new Date();
  const milnikDate = new Date(milnik.datum + "T00:00:00");
  const daysToMilnik = Math.max(0, Math.round((milnikDate - today) / 86400000));
  // Měsíční dopad navýšení = PRŮMĚRNÝ počet tréninků za měsíc × navýšení sazby.
  // Dřív se bral aktuální (rozdělaný) měsíc → na začátku měsíce mikro částka. Průměr je reálnější.
  const avgMes = avgTreninkyMesic != null ? avgTreninkyMesic : (treninkyMesic || 0);
  const monthlyImpact = avgMes * milnik.naroustOKc;

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 sm:p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <Target className="w-4 h-4" />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-600 font-bold leading-tight flex-1 min-w-0 inline-flex items-center gap-1.5 flex-wrap">
          <span className="truncate">Sazba a postup</span>
          <InfoTip text={`Tvoje aktuální sazba a kdy se zvedne dle smlouvy (Příloha A.2). Příští milník: ${fmtCzDate(milnik.datum)} (${sazbaInfo.mesicSpoluprace + Math.ceil(daysToMilnik / 30)}. měsíc) → +${milnik.naroustOKc} Kč/jednotka. ${monthlyImpact > 0 ? `Při tvém průměru ${avgMes} tréninků/měs to bude +${new Intl.NumberFormat("cs-CZ").format(monthlyImpact)} Kč/měs.` : ""}`} />
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="score-val font-black text-slate-900 tabular-nums leading-none text-2xl sm:text-3xl">{sazbaActual}</span>
        <span className="text-sm font-bold text-slate-500">Kč/jed.</span>
      </div>
      <div className="score-val mt-2.5 inline-flex items-center gap-1.5 bg-emerald-100 border border-emerald-300 text-emerald-800 px-2.5 py-1 rounded-lg shadow-sm">
        <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-sm font-black tabular-nums">{milnik.sazba} Kč</span>
        <span className="text-xs font-bold text-emerald-700 tabular-nums">(+{milnik.naroustOKc} Kč)</span>
        <span className="text-xs text-emerald-700">za</span>
        <span className="text-sm font-black tabular-nums">{daysToMilnik === 0 ? "dnes" : `${daysToMilnik} ${daysToMilnik === 1 ? "den" : daysToMilnik < 5 ? "dny" : "dní"}`}</span>
      </div>
      {monthlyImpact > 0 && (
        <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">
          To bude <span className="score-val font-bold text-slate-700 tabular-nums">+{new Intl.NumberFormat("cs-CZ").format(monthlyImpact)} Kč/měs</span> při tvém tempu
        </div>
      )}
    </div>
  );
}

function PostupKVyssiSazbeCard({ sazbaInfo, sazbaActual, treninkyMesic }) {
  const milnik = sazbaInfo.pristiMilnik;
  const fmtCzDate = (iso) => {
    if (!iso) return "-";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}` : iso;
  };

  // Speciální case: smluvní výjimka mimo standardní vzorec (např. Reichrich do certifikace).
  // Není to "dosáhl top sazby", ale pevná individuální dohoda — zobrazíme text z důvodu.
  const isVyjimka = sazbaInfo.source === "vyjimka_hardcoded" || sazbaInfo.source === "vyjimka_explicitni";
  if (isVyjimka) {
    return (
      <div className="bg-gradient-to-br from-brand-50 to-brand-50 border border-brand-200 rounded-xl p-5 shadow-sm flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-brand-200 text-brand-700 flex items-center justify-center">
            <Award className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
              Smluvní výjimka
              <InfoTip text="Tvoje sazba je mimo standardní vzorec (Příloha A.2 smlouvy), individuální dohoda. Pro úpravu kontaktuj managera." />
            </div>
            <div className="text-xs text-slate-500">{sazbaInfo.mesicSpoluprace}. měsíc spolupráce</div>
          </div>
        </div>
        <div className="text-3xl font-black tabular-nums text-brand-700">{sazbaActual} Kč/jednotka</div>
        {sazbaInfo.varovani && (
          <div className="text-sm text-slate-700 mt-2 leading-relaxed">{sazbaInfo.varovani}</div>
        )}
      </div>
    );
  }

  // Pokud trenér je v nejvyšší sazbě (top stupeň smlouvy)
  if (!milnik) {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-amber-50 border border-amber-200 rounded-xl p-5 shadow-sm flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-amber-200 text-amber-700 flex items-center justify-center">
            <Award className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
              Maximální sazba
              <InfoTip text="Jsi v nejvyšší sazbě smlouvy (po 36 měsících spolupráce). Sazba se už dál nezvyšuje." />
            </div>
            <div className="text-xs text-slate-500">{sazbaInfo.mesicSpoluprace}. měsíc spolupráce</div>
          </div>
        </div>
        <div className="text-3xl font-black tabular-nums text-amber-700">{sazbaActual} Kč/jednotka</div>
        <div className="text-sm text-slate-600 mt-2">Dosáhl jsi nejvyšší sazby. Gratulujeme!</div>
      </div>
    );
  }

  const today = new Date();
  const milnikDate = new Date(milnik.datum + "T00:00:00");
  const daysToMilnik = Math.max(0, Math.round((milnikDate - today) / 86400000));
  const monthlyImpact = (treninkyMesic || 0) * milnik.naroustOKc;

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 shadow-sm flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-emerald-200 text-emerald-700 flex items-center justify-center">
          <Target className="w-4 h-4" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
            Postup k vyšší sazbě
            <InfoTip text="Kdy se ti zvedne sazba dle smlouvy. Vzorec: po dosažení dalšího milníku spolupráce (3, 5, 7, 10, 12, 15, 18, 24, 36 měsíců) sazba roste o pevnou částku. Bonus zahrnut." />
          </div>
          <div className="text-xs text-slate-500">{sazbaInfo.mesicSpoluprace}. měsíc spolupráce</div>
        </div>
      </div>

      {/* Aktuálně vs příští */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-white/70 rounded-xl p-3 border border-emerald-100">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Aktuálně</div>
          <div className="text-2xl font-black tabular-nums text-slate-800 leading-tight">{sazbaActual} Kč</div>
          <div className="text-[11px] text-slate-400 mt-0.5">/jednotka</div>
        </div>
        <div className="bg-emerald-100 rounded-xl p-3 border border-emerald-300 ring-2 ring-emerald-200/50">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Příští sazba</div>
          <div className="text-2xl font-black tabular-nums text-emerald-700 leading-tight">{milnik.sazba} Kč</div>
          <div className="text-[11px] text-emerald-600 mt-0.5 font-bold">+{milnik.naroustOKc} Kč</div>
        </div>
      </div>

      {/* Datum + countdown */}
      <div className="bg-white/70 rounded-xl p-3 border border-emerald-100">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <span className="text-xs text-slate-600 font-semibold">Dosáhneš {fmtCzDate(milnik.datum)}</span>
          <span className="text-sm font-black text-emerald-700 tabular-nums">{daysToMilnik === 0 ? "DNES" : `za ${daysToMilnik} dní`}</span>
        </div>
        {monthlyImpact > 0 && (
          <div className="text-[11px] text-slate-500 leading-snug">
            Při tvých {treninkyMesic} trénincích/měsíc to bude
            <span className="font-bold text-emerald-700"> +{new Intl.NumberFormat("cs-CZ").format(monthlyImpact)} Kč/měs</span>
          </div>
        )}
      </div>
    </div>
  );
}

// === NOTIFIKACE — sekce pod rozvrhem (stejný feed jako zvoneček v hlavičce) ===
// Taby Aktivní / Odložené / Archiv, vyřídit / odložit / vrátit. Čte /api/trener-notifikace.
const SECTION_NOTIF_META = {
  ukol:      { Icon: ListTodo, dot: "bg-brand-400" },
  dokument:  { Icon: FileText, dot: "bg-amber-400" },
  neaktivni: { Icon: UserX, dot: "bg-rose-400" },
  konec:     { Icon: Clock, dot: "bg-red-500" },
  vycerpano: { Icon: CalendarX, dot: "bg-red-500" },
  denik:     { Icon: Trophy, dot: "bg-amber-400" },
  zminka:    { Icon: MessageSquare, dot: "bg-violet-400" },
  vyplata:   { Icon: Receipt, dot: "bg-emerald-400" },
  faktura:   { Icon: Receipt, dot: "bg-amber-400" },
};
function TrenerNotifikaceSection({ activeTrener }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("aktivni");
  const [active, setActive] = useState([]);
  const [archiveAll, setArchiveAll] = useState([]);
  const [archiveCount, setArchiveCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const pilotQ = activeTrener ? `?pilot=${encodeURIComponent(activeTrener)}` : "";

  const loadActive = async () => {
    if (!activeTrener) return;
    try {
      const r = await apiFetch(`/api/trener-notifikace${pilotQ}`);
      const j = await r.json();
      setActive(j.active || []); setArchiveCount(j.archiveCount || 0); setLoaded(true);
    } catch { setLoaded(true); }
  };
  const loadArchive = async () => {
    try {
      const r = await apiFetch(`/api/trener-notifikace${pilotQ}${pilotQ ? "&" : "?"}archive=1`);
      const j = await r.json();
      setArchiveAll(j.items || []);
    } catch { /* ticho */ }
  };
  useEffect(() => { loadActive(); /* eslint-disable-next-line */ }, [activeTrener]);
  useEffect(() => { if (tab !== "aktivni") loadArchive(); /* eslint-disable-next-line */ }, [tab, activeTrener]);

  const act = async (notif_key, action, opts = {}) => {
    setBusy(true);
    setActive((a) => a.filter((x) => x.notif_key !== notif_key));
    try {
      await apiFetch("/api/trener-notifikace", { method: "POST", body: JSON.stringify({ action, notif_key, ...opts }) });
      await loadActive(); if (tab !== "aktivni") await loadArchive();
    } catch { await loadActive(); } finally { setBusy(false); }
  };
  const goTo = (link) => { if (link) navigate(link); };

  if (!activeTrener) return null;
  const odlozene = archiveAll.filter((n) => n._stav === "snoozed");
  const vyrizene = archiveAll.filter((n) => n._stav !== "snoozed");
  const list = tab === "aktivni" ? active : tab === "odlozene" ? odlozene : vyrizene;

  // Přepínač na celou šířku, aktivní záložka je bílá dlaždice na šedé dráze.
  // Vzor: přepínač Aktivní / Odložené / Archiv v PM appce.
  const TabBtn = ({ id, label, count }) => (
    <button
      onClick={() => setTab(id)}
      aria-pressed={tab === id}
      className={`inline-flex min-h-[44px] min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl px-2 text-sm font-semibold transition-colors ${
        tab === id ? "bg-white text-brand-deep shadow-sm" : "text-slate-500"
      }`}
    >
      <span className="truncate">{label}</span>
      {count > 0 ? (
        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs font-bold tabular-nums ${tab === id ? "bg-brand-light text-brand-deep" : "bg-slate-200 text-slate-600"}`}>{count}</span>
      ) : null}
    </button>
  );

  return (
    <div className="glass min-w-0 overflow-hidden p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-brand-100 px-3 py-2">
        <Bell className="h-4 w-4 shrink-0 text-brand-deep" />
        <span className="min-w-0 flex-1 text-sm font-bold text-brand-950">Notifikace</span>
      </div>
      <div className="mb-3 flex items-center gap-1 rounded-xl bg-slate-100 p-1">
        <TabBtn id="aktivni" label="Aktivní" count={active.length} />
        <TabBtn id="odlozene" label="Odložené" count={odlozene.length} />
        <TabBtn id="archiv" label="Archiv" count={archiveCount - odlozene.length > 0 ? archiveCount - odlozene.length : (vyrizene.length || 0)} />
      </div>
      <div className="divide-y divide-slate-100">
        {list.length === 0 ? (
          <div className="inline-flex w-full items-center justify-center gap-2 px-4 py-8 text-center text-sm text-slate-400">
            {tab === "aktivni" ? <><Check className="w-4 h-4 text-emerald-400" /> Žádné aktivní notifikace, vše pod kontrolou.</> : "Nic tady."}
          </div>
        ) : list.map((n) => {
          const meta = SECTION_NOTIF_META[n.typ] || SECTION_NOTIF_META.ukol;
          const isActive = tab === "aktivni";
          return (
            <div key={n.notif_key} className="py-3">
              <div className="flex items-start gap-3">
                {isActive ? <span className={`w-2 h-2 rounded-full mt-2 shrink-0 ${meta.dot}`} /> : <meta.Icon className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                <button onClick={() => goTo(n.link)} className="min-w-0 flex-1 text-left">
                  <div className={`text-sm font-semibold leading-snug ${isActive ? "text-slate-800" : "text-slate-500 line-through decoration-slate-300"}`}>{n.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{isActive ? n.detail : (n._stav === "snoozed" ? `Odloženo do ${n._snooze_until}` : "Vyřízeno")}</div>
                </button>
                {isActive ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => act(n.notif_key, "done")} disabled={busy} className="inline-flex items-center gap-1 text-sm sm:text-xs font-semibold px-3 py-2 sm:px-2.5 sm:py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 active:bg-emerald-200 disabled:opacity-50 transition-colors"><Check className="w-4 h-4 sm:w-3.5 sm:h-3.5" /> Vyřídit</button>
                    <SnoozeButton onPick={(o) => act(n.notif_key, "snooze", o)} disabled={busy} />
                  </div>
                ) : (
                  <button onClick={() => act(n.notif_key, "reopen")} disabled={busy} className="text-xs font-semibold text-brand-600 hover:text-brand-800 shrink-0">Vrátit</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
